// Public event-ticket claim page (SLICE-EVENT-TICKET-PASS). Mounted at
// /e/events/:code. The shareable self-claim surface: shows the event + tiers,
// collects an optional name + PH-DPA consent, claims one ticket, and offers
// Add-to-Wallet. Mirrors EnrollPage; unauthenticated.

import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'

import addToGoogleWalletButton from './assets/add-to-google-wallet.svg'
import addToAppleWalletButton from './assets/add-to-apple-wallet.svg'
import { saveLinkFor, shouldUseApple } from './walletPlatform'
import './enrollTokens.css'

interface ClaimTier {
  id: string
  name: string
  price_centavos: number | null
  remaining: number | null
  sold_out: boolean
}
interface EventGetResponse {
  business_name: string
  program_id: string
  event: {
    event_name: string
    starts_at: string
    ends_at: string | null
    venue_name: string
    venue_address: string | null
    holder_name_required: boolean
  }
  tiers: ClaimTier[]
  sold_out: boolean
}
interface ClaimResponse {
  card_id: string
  tier_name: string
  save_url: string
  providers_available?: string[]
}

function formatWhen(iso: string): string {
  const t = Date.parse(iso)
  if (Number.isNaN(t)) return ''
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Manila',
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(t))
}
function peso(centavos: number | null): string {
  if (centavos == null) return 'Free'
  return `₱${(centavos / 100).toLocaleString('en-PH', {
    minimumFractionDigits: centavos % 100 === 0 ? 0 : 2,
  })}`
}

export function EventClaimPage() {
  const { code } = useParams<{ code: string }>()
  const [config, setConfig] = useState<EventGetResponse | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [tierId, setTierId] = useState('')
  const [name, setName] = useState('')
  const [consent, setConsent] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [fieldError, setFieldError] = useState<string | null>(null)
  const [success, setSuccess] = useState<ClaimResponse | null>(null)

  useEffect(() => {
    if (!code) return
    fetch(`/api/public/events/${encodeURIComponent(code)}`, {
      credentials: 'omit',
    })
      .then(async (res) => {
        const body = await res.json().catch(() => null)
        if (!res.ok) {
          throw new Error(
            (body as { error?: { message?: string } } | null)?.error?.message ??
              'This event could not be found.',
          )
        }
        const data = body as EventGetResponse
        setConfig(data)
        const firstAvailable = data.tiers.find((t) => !t.sold_out) ?? data.tiers[0]
        if (firstAvailable) setTierId(firstAvailable.id)
      })
      .catch((err: Error) => setLoadError(err.message))
  }, [code])

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!config || !consent || !code) return
    if (config.event.holder_name_required && !name.trim()) {
      setFieldError('Please enter your name.')
      return
    }
    setSubmitting(true)
    setFieldError(null)
    try {
      const res = await fetch(
        `/api/public/events/${encodeURIComponent(code)}/claim`,
        {
          method: 'POST',
          credentials: 'omit',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...(tierId ? { tier_id: tierId } : {}),
            ...(name.trim() ? { holder_name: name.trim() } : {}),
            consented_at: new Date().toISOString(),
          }),
        },
      )
      const body = await res.json().catch(() => null)
      if (!res.ok) {
        const message =
          (body as { error?: { message?: string } } | null)?.error?.message ??
          'Could not claim your ticket.'
        setFieldError(message)
        return
      }
      setSuccess(body as ClaimResponse)
    } catch {
      setFieldError('Network error — please try again.')
      toast.error('Network error — please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  if (loadError) {
    return (
      <div className="enroll-screen">
        <div className="enroll-card">
          <h2>Event unavailable</h2>
          <p>{loadError}</p>
        </div>
      </div>
    )
  }
  if (!config) {
    return (
      <div className="enroll-screen">
        <div className="enroll-card">
          <p>Loading…</p>
        </div>
      </div>
    )
  }

  if (success) {
    const useApple = shouldUseApple(success.providers_available)
    return (
      <div className="enroll-screen">
        <div className="enroll-card enroll-card-success">
          <div className="enroll-success">
            <h2>You're in!</h2>
            <p>
              Add your <strong>{success.tier_name}</strong> ticket to{' '}
              <strong>{config.event.event_name}</strong> to your wallet.
            </p>
            <a
              href={saveLinkFor(success.save_url, useApple)}
              className="enroll-wallet-button"
            >
              <img
                src={useApple ? addToAppleWalletButton : addToGoogleWalletButton}
                alt={useApple ? 'Add to Apple Wallet' : 'Add to Google Wallet'}
              />
            </a>
          </div>
        </div>
      </div>
    )
  }

  const eventSoldOut = config.sold_out
  const formValid =
    consent &&
    !eventSoldOut &&
    (!config.event.holder_name_required || name.trim().length > 0)

  return (
    <div className="enroll-screen">
      <div className="enroll-card">
        <h2>{config.event.event_name}</h2>
        <p className="enroll-sub">
          {formatWhen(config.event.starts_at)}
          <br />
          {config.event.venue_name}
          {config.event.venue_address ? `, ${config.event.venue_address}` : ''}
        </p>

        {eventSoldOut ? (
          <p className="enroll-error">This event is sold out.</p>
        ) : (
          <form className="enroll-form" onSubmit={onSubmit} noValidate>
            {config.tiers.length > 1 && (
              <div className="enroll-field">
                <label htmlFor="claim-tier">Ticket tier</label>
                <select
                  id="claim-tier"
                  value={tierId}
                  onChange={(e) => setTierId(e.target.value)}
                >
                  {config.tiers.map((t) => (
                    <option key={t.id} value={t.id} disabled={t.sold_out}>
                      {t.name} · {peso(t.price_centavos)}
                      {t.sold_out ? ' — sold out' : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="enroll-field">
              <label htmlFor="claim-name">
                Your name
                {config.event.holder_name_required && (
                  <span className="enroll-required"> *</span>
                )}
              </label>
              <input
                id="claim-name"
                type="text"
                name="name"
                autoComplete="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Maria Santos"
              />
            </div>

            <label className="enroll-consent">
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                required
              />
              <span>
                I agree to Naya Pass's{' '}
                <a href="https://pass.naya.ph/terms" target="_blank" rel="noreferrer">
                  Terms
                </a>{' '}
                and{' '}
                <a
                  href="https://pass.naya.ph/privacy"
                  target="_blank"
                  rel="noreferrer"
                >
                  Privacy Policy
                </a>
                , and consent to <strong>{config.business_name}</strong> using my
                details for this event.
              </span>
            </label>

            {fieldError && <p className="enroll-error">{fieldError}</p>}

            <button
              type="submit"
              className="enroll-submit"
              disabled={!formValid || submitting}
            >
              {submitting ? 'Getting your ticket…' : 'Get my ticket'}
            </button>
          </form>
        )}
      </div>
    </div>
  )
}
